import React, { FC, useState } from 'react';
import { t } from 'i18next';
import { uniq } from 'lodash';

import { AudienceScope, EntityType } from 'Features/audience/pages/AudienceGroupList/types';
import { AudiencePlayerList } from 'Features/audience/pages/AudiencePlayerList/AudiencePlayerList';
import SInput from 'Components/structural/SInput/SInput';
import UTextLink from 'Components/unit/UTextLink/UTextLink';
import { handleFileSheet } from 'Libs/excel-parser';
import { enqueueBasicAlert } from 'Pages/AlertManager';

import styles from './AudienceEmailList.style';

export type AudienceEmailListProps = {
  entityType: EntityType;
  entityId: number;
  scope: AudienceScope;
  update: (value: { addedEmails: string[]; removedEmails: string[] }) => void;
  value?: { addedEmails: string[]; removedEmails: string[] };
  hasAudienceTypeChanged: boolean;
};

const ADD_INPUT_ICON = {
  icon: 'plus',
  ghost: false,
  type: 'accentuated',
} as const;
export const AudienceEmailList: FC<AudienceEmailListProps> = ({
  entityId,
  entityType,
  scope,
  hasAudienceTypeChanged,
  value,
  update,
}) => {
  const [email, setEmail] = useState<string>('');

  const handleSubmit = (email: string) => {
    if (email) {
      update({ addedEmails: uniq([...(value?.addedEmails || []), email]), removedEmails: value?.removedEmails || [] });
      setEmail('');
    }
  };

  const handleImportButtonClick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      return;
    }
    const file = files[0];

    try {
      const stringOfEmails = await handleFileSheet(file);
      const emails = stringOfEmails.split(',').filter((item) => item !== '');

      if (emails.length > 0) {
        update({
          removedEmails: value?.removedEmails || [],
          addedEmails: uniq([...(value?.addedEmails || []), ...emails]),
        });
      }
    } catch (e) {
      enqueueBasicAlert({
        id: 'import-email-list-error',
        status: 'error',
        title: t('activities:audience.email_list.invalid_file'),
      });
    }
  };

  const handleDeleteAdditionalEmails = (player: { id: string }) => {
    update({
      addedEmails: value?.addedEmails?.filter((item) => item !== player.id) || [],
      removedEmails: value?.removedEmails || [],
    });
    setEmail('');
  };

  const handleDeleteEmail = (playerEmail: string) => {
    update({
      removedEmails: uniq([...(value?.removedEmails || []), playerEmail]),
      addedEmails: value?.addedEmails || [],
    });
  };

  return (
    <div style={styles.infoWrapper}>
      <div style={styles.title}>{t('activities:audience.email_list.title')}</div>
      <div style={styles.subtitle}>{t('activities:audience.email_list.subtitle')}</div>
      <div style={styles.email}>
        <SInput
          type="small"
          placeholder={t('activities:audience.email_list.email_placeholder')}
          icon={ADD_INPUT_ICON}
          value={email}
          onSubmit={handleSubmit}
          onIconClick={handleSubmit}
          onChange={(value) => setEmail(value)}
          style={styles.emailInput}
        />
        <label>
          <UTextLink text={t('activities:audience.email_list.upload_list')} onClick={() => {}} marginLeft={0} />
          <input style={styles.fileHandler} type="file" accept=".csv,.xlsx,.txt" onChange={handleImportButtonClick} />
        </label>
      </div>

      <AudiencePlayerList
        entityId={entityId}
        deletePlayer={handleDeleteEmail}
        deleteAdditionalPlayer={handleDeleteAdditionalEmails}
        entityType={entityType}
        additionalEmails={value?.addedEmails || []}
        removedEmails={value?.removedEmails || []}
        scope={scope}
        audienceHasBeenReset={hasAudienceTypeChanged}
      />
    </div>
  );
};
